import React, { useState, useRef, useEffect } from 'react';
import { Edit2, RotateCcw, Check, X } from 'lucide-react';

interface EditableColumnHeaderProps {
  columnKey: string;
  displayName: string;
  defaultName: string;
  isCustomized: boolean;
  className?: string;
  onSave: (columnKey: string, newName: string) => Promise<void>;
  onReset: (columnKey: string) => Promise<void>;
  disabled?: boolean;
}

const EditableColumnHeader: React.FC<EditableColumnHeaderProps> = ({
  columnKey,
  displayName,
  defaultName,
  isCustomized,
  className = '',
  onSave,
  onReset,
  disabled = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(displayName);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Reset edit value when displayName changes
  useEffect(() => {
    setEditValue(displayName);
  }, [displayName]);

  const handleDoubleClick = () => {
    if (!disabled) {
      setIsEditing(true);
      setError(null);
    }
  };

  const handleSave = async () => {
    const trimmedValue = editValue.trim();
    
    // Validation
    if (!trimmedValue) {
      setError('Column name cannot be empty');
      return;
    }

    if (trimmedValue.length > 200) {
      setError('Column name is too long (max 200 characters)');
      return;
    }

    if (trimmedValue === displayName) {
      // No change, just exit edit mode
      setIsEditing(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await onSave(columnKey, trimmedValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save column name:', error);
      setError('Failed to save column name');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEditValue(displayName);
    setIsEditing(false);
    setError(null);
  };

  const handleReset = async () => {
    if (!isCustomized) return;

    setIsLoading(true);
    setError(null);

    try {
      await onReset(columnKey);
    } catch (error) {
      console.error('Failed to reset column name:', error);
      setError('Failed to reset column name');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className={`relative ${className}`}>
        <div className="flex items-center space-x-1">
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            disabled={isLoading}
            className="text-xs font-medium text-gray-900 bg-white border border-blue-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-0 flex-1"
            placeholder="Column name..."
          />
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="p-0.5 text-green-600 hover:text-green-800 disabled:opacity-50"
            title="Save"
          >
            <Check className="h-3 w-3" />
          </button>
          <button
            onClick={handleCancel}
            disabled={isLoading}
            className="p-0.5 text-red-600 hover:text-red-800 disabled:opacity-50"
            title="Cancel"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
        {error && (
          <div className="absolute top-full left-0 mt-1 text-xs text-red-600 bg-white border border-red-200 rounded px-2 py-1 shadow-sm z-10">
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`group relative ${className}`}>
      <div 
        className="flex items-center space-x-1 cursor-pointer"
        onDoubleClick={handleDoubleClick}
        title={`Double-click to edit${isCustomized ? ` (Original: ${defaultName})` : ''}`}
      >
        <span className={`text-xs font-medium uppercase tracking-wider ${
          isCustomized ? 'text-blue-600' : 'text-gray-500'
        }`}>
          {displayName}
          {isCustomized && (
            <span className="ml-1 inline-block w-1.5 h-1.5 bg-blue-500 rounded-full" title="Customized" />
          )}
        </span>
        
        {!disabled && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center space-x-0.5">
            <button
              onClick={handleDoubleClick}
              disabled={isLoading}
              className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-50"
              title="Edit column name"
            >
              <Edit2 className="h-3 w-3" />
            </button>
            {isCustomized && (
              <button
                onClick={handleReset}
                disabled={isLoading}
                className="p-0.5 text-gray-400 hover:text-orange-600 disabled:opacity-50"
                title={`Reset to "${defaultName}"`}
              >
                <RotateCcw className="h-3 w-3" />
              </button>
            )}
          </div>
        )}
      </div>
      
      {error && (
        <div className="absolute top-full left-0 mt-1 text-xs text-red-600 bg-white border border-red-200 rounded px-2 py-1 shadow-sm z-10">
          {error}
        </div>
      )}
    </div>
  );
};

export default EditableColumnHeader;




